/* eslint-disable no-underscore-dangle */

import * as Events from 'events';

import { PressableProps } from '@scatterlab/zeta-core';
import {
  ComponentProps,
  CSSProperties,
  FunctionComponent,
  ReactNode,
} from 'react';

import { CheckIcon, Warning16Icon } from '../icons';

export interface ToastArguments {
  id?: string;
  message: string;
  subMessage?: string;
  onPress?: OnClickListener;
  buttonText?: string;
  icon?: FunctionComponent<ComponentProps<'svg'> & { title?: string }>;
  className?: string;
  style?: CSSProperties;
  duration?: number;
  position?: 'top' | 'bottom';
  rightElement?: ReactNode | ((dismiss: () => void) => ReactNode);
  loading?: boolean;
}

export type Toast = Omit<ToastArguments, 'id'> &
  Required<Pick<ToastArguments, 'id'>>;

type OnClickListener = Required<PressableProps>['onPress'];

type AddListener = (toast: Toast) => void;
type RemoveListener = (id: string, reason: 'button' | 'timeout') => void;

type EventMap = {
  add: AddListener;
  remove: RemoveListener;
};

const eventEmitter = new Events.EventEmitter();

const removeEventListener = <
  EventName extends keyof EventMap,
  EventListener extends EventMap[EventName],
>(
  eventName: EventName,
  listener: EventListener,
) => {
  eventEmitter.removeListener(eventName, listener);
};

const addEventListener = <
  EventName extends keyof EventMap,
  EventListener extends EventMap[EventName],
>(
  eventName: EventName,
  listener: EventListener,
) => {
  eventEmitter.addListener(eventName, listener);

  return () => {
    removeEventListener(eventName, listener);
  };
};

const once = <
  EventName extends keyof EventMap,
  EventListener extends EventMap[EventName],
>(
  eventName: EventName,
  listener: EventListener,
  targetId?: string,
) => {
  if (eventName === 'add') {
    const addListener: AddListener = (toast) => {
      if (targetId && targetId !== toast.id) return;
      eventEmitter.removeListener(eventName, addListener);
      (listener as AddListener)(toast);
    };
    eventEmitter.addListener(eventName, addListener);
    return () => {
      removeEventListener(eventName as 'add', addListener);
    };
  }
  if (eventName === 'remove') {
    const removeListener: RemoveListener = (id, reason) => {
      if (targetId && targetId !== id) return;
      eventEmitter.removeListener(eventName, removeListener);
      (listener as RemoveListener)(id, reason);
    };
    eventEmitter.addListener(eventName, removeListener);
    return () => {
      removeEventListener(eventName as 'remove', removeListener);
    };
  }
  return () => {};
};

const remove = (id: string, reason: 'button' | 'timeout') => {
  eventEmitter.emit('remove', id, reason);
};

const base = ({
  id = Math.random().toString(36).slice(2),
  duration = 3000,
  position = 'bottom',
  ...toastOptions
}: ToastArguments) => {
  eventEmitter.emit('add', { id, position, ...toastOptions });

  setTimeout(() => {
    remove(id, 'timeout');
  }, duration);

  return id;
};

const success = (
  message: string,
  options: Omit<ToastArguments, 'message'> = {},
) => base({ message, icon: CheckIcon, ...options });

const error = (
  message: string,
  options: Omit<ToastArguments, 'message'> = {},
) => base({ message, icon: Warning16Icon, ...options });

const info = (message: string, options: Omit<ToastArguments, 'message'> = {}) =>
  base({ message, ...options });

export default {
  addEventListener,
  removeEventListener,
  once,
  remove,
  base,
  success,
  error,
  info,
};
